import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	Typography,
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Alert,
	CircularProgress,
} from "@mui/material";
import {
	Edit as EditIcon,
	Plus as PlusIcon,
	Trash2 as TrashIcon,
	Save as SaveIcon,
	X as CancelIcon,
} from "lucide-react";
import { getContentDisplayTitle } from "../../utils/contentDisplayTitles";

const QUIZ_FIELDS = [
	{ name: "question", label: "Question", type: "text", required: true },
	{ name: "option_a", label: "Option A", type: "text", required: true },
	{ name: "option_b", label: "Option B", type: "text", required: true },
	{ name: "option_c", label: "Option C", type: "text", required: true },
	{ name: "option_d", label: "Option D", type: "text", required: true },
	{
		name: "correct_answer",
		label: "Correct Answer",
		type: "select",
		options: [
			{ value: "a", label: "A" },
			{ value: "b", label: "B" },
			{ value: "c", label: "C" },
			{ value: "d", label: "D" },
		],
		required: true,
	},
	{
		name: "quiz_order",
		label: "Quiz Order",
		type: "number",
		required: true,
		min: 1,
	},
];

const GameContentQuizzes = ({ open, onClose, content, supabase }) => {
	const [quizzes, setQuizzes] = useState([]);
	const [editMode, setEditMode] = useState(false);
	const [selectedQuiz, setSelectedQuiz] = useState(null);
	const [quizForm, setQuizForm] = useState({});
	const [formErrors, setFormErrors] = useState({});
	const [successMessage, setSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	// Fetch quizzes for the selected content
	const fetchQuizzes = async () => {
		try {
			setIsLoading(true);
			const { data, error } = await supabase
				.from("game_content_quizzes")
				.select("*")
				.eq("content_id", content.id)
				.order("quiz_order");

			if (error) throw error;
			setQuizzes(data || []);
		} catch (err) {
			console.error("Error fetching quizzes:", err);
			setErrorMessage("Error loading quizzes");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (open && content) {
			fetchQuizzes();
		}
	}, [open, content]);

	const validateForm = () => {
		const errors = {};
		QUIZ_FIELDS.forEach((field) => {
			if (field.required && !quizForm[field.name]) {
				errors[field.name] = `${field.label} is required`;
			}
		});

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleAddQuiz = () => {
		// Find the next available quiz order
		const nextOrder =
			quizzes.length > 0
				? Math.max(...quizzes.map((q) => q.quiz_order || 0)) + 1
				: 1;

		setSelectedQuiz(null);
		setQuizForm({
			content_id: content.id,
			correct_answer: "a", // Default value
			quiz_order: nextOrder,
		});
		setFormErrors({});
		setEditMode(true);
	};

	const handleEditQuiz = (quiz) => {
		setSelectedQuiz(quiz);
		setQuizForm({ ...quiz });
		setFormErrors({});
		setEditMode(true);
	};

	const handleCancelEdit = () => {
		setEditMode(false);
		setSelectedQuiz(null);
		setQuizForm({});
		setFormErrors({});
	};

	const handleDeleteQuiz = async (id) => {
		if (!window.confirm("Are you sure you want to delete this quiz?")) return;

		try {
			const { error } = await supabase
				.from("game_content_quizzes")
				.delete()
				.eq("id", id);

			if (error) throw error;

			// After deleting, reorder the remaining quizzes if needed
			const remainingQuizzes = quizzes.filter((q) => q.id !== id);
			remainingQuizzes.sort((a, b) => a.quiz_order - b.quiz_order);

			for (let i = 0; i < remainingQuizzes.length; i++) {
				const quiz = remainingQuizzes[i];
				const newOrder = i + 1;

				if (quiz.quiz_order !== newOrder) {
					await supabase
						.from("game_content_quizzes")
						.update({ quiz_order: newOrder })
						.eq("id", quiz.id);
				}
			}

			setSuccessMessage("Quiz deleted successfully");
			fetchQuizzes();

			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error("Error deleting quiz:", err);
			setErrorMessage("Error deleting quiz");
		}
	};

	const handleSaveQuiz = async () => {
		try {
			if (!validateForm()) {
				return;
			}

			setIsSaving(true);
			const formToSave = { ...quizForm };

			if (selectedQuiz) {
				// Update
				const { error } = await supabase
					.from("game_content_quizzes")
					.update(formToSave)
					.eq("id", selectedQuiz.id);

				if (error) throw error;
				setSuccessMessage("Quiz updated successfully");
			} else {
				// Insert
				const { error } = await supabase
					.from("game_content_quizzes")
					.insert([formToSave]);

				if (error) throw error;
				setSuccessMessage("New quiz added successfully");
			}

			setEditMode(false);
			fetchQuizzes();

			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error("Error saving quiz:", err);
			setErrorMessage("Error saving quiz");
		} finally {
			setIsSaving(false);
		}
	};

	const renderField = (field) => {
		if (field.type === "select") {
			return (
				<FormControl fullWidth key={field.name} sx={{ mt: 2 }}>
					<InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
					<Select
						labelId={`${field.name}-label`}
						value={quizForm[field.name] || ""}
						onChange={(e) =>
							setQuizForm({
								...quizForm,
								[field.name]: e.target.value,
							})
						}
						label={field.label}
						error={!!formErrors[field.name]}
						required={field.required}
						disabled={isSaving}>
						{field.options.map((option) => (
							<MenuItem
								key={typeof option === "object" ? option.value : option}
								value={typeof option === "object" ? option.value : option}>
								{typeof option === "object"
									? option.label
									: option.toUpperCase()}
							</MenuItem>
						))}
					</Select>
					{formErrors[field.name] && (
						<Typography variant="caption" color="error">
							{formErrors[field.name]}
						</Typography>
					)}
				</FormControl>
			);
		}

		if (field.type === "number") {
			return (
				<TextField
					key={field.name}
					label={field.label}
					type="number"
					fullWidth
					value={quizForm[field.name] || ""}
					onChange={(e) =>
						setQuizForm({
							...quizForm,
							[field.name]: parseInt(e.target.value) || "",
						})
					}
					InputProps={{ inputProps: { min: field.min || 1 } }}
					error={!!formErrors[field.name]}
					helperText={formErrors[field.name]}
					required={field.required}
					disabled={isSaving}
					sx={{ mt: 2 }}
				/>
			);
		}

		return (
			<TextField
				key={field.name}
				label={field.label}
				fullWidth
				multiline={field.name === "question"}
				rows={field.name === "question" ? 2 : 1}
				value={quizForm[field.name] || ""}
				onChange={(e) =>
					setQuizForm({
						...quizForm,
						[field.name]: e.target.value,
					})
				}
				error={!!formErrors[field.name]}
				helperText={formErrors[field.name]}
				required={field.required}
				disabled={isSaving}
				sx={{ mt: 2 }}
			/>
		);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}>
					<Typography variant="h6">
						Manage Quizzes for: {getContentDisplayTitle(content.title)}
					</Typography>
					{!editMode && (
						<Button
							variant="contained"
							startIcon={<PlusIcon size={18} />}
							onClick={handleAddQuiz}>
							Add Quiz
						</Button>
					)}
				</Box>
			</DialogTitle>
			<DialogContent>
				{successMessage && (
					<Alert severity="success" sx={{ mb: 2 }}>
						{successMessage}
					</Alert>
				)}
				{errorMessage && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{errorMessage}
					</Alert>
				)}

				{editMode ? (
					// Quiz Edit Form
					<Paper
						elevation={0}
						sx={{ p: 2, border: "1px solid", borderColor: "divider", mt: 2 }}>
						<Typography variant="subtitle1" sx={{ mb: 2 }}>
							{selectedQuiz ? "Edit Quiz" : "Add New Quiz"}
						</Typography>
						{QUIZ_FIELDS.map((field) => renderField(field))}
						<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
							<Button
								variant="outlined"
								startIcon={<CancelIcon size={18} />}
								onClick={handleCancelEdit}
								sx={{ mr: 1 }}
								disabled={isSaving}>
								Cancel
							</Button>
							<Button
								variant="contained"
								startIcon={
									isSaving ? (
										<CircularProgress size={16} color="inherit" />
									) : (
										<SaveIcon size={18} />
									)
								}
								onClick={handleSaveQuiz}
								disabled={isSaving}>
								{isSaving ? "Saving..." : selectedQuiz ? "Update" : "Save"}
							</Button>
						</Box>
					</Paper>
				) : (
					// Quiz List
					<>
						{isLoading ? (
							<Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
								<CircularProgress />
							</Box>
						) : quizzes.length > 0 ? (
							<TableContainer component={Paper} variant="outlined">
								<Table>
									<TableHead>
										<TableRow>
											<TableCell width="5%">#</TableCell>
											<TableCell>Question</TableCell>
											<TableCell width="15%">Correct Answer</TableCell>
											<TableCell width="15%" align="right">
												Actions
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{quizzes.map((quiz) => (
											<TableRow key={quiz.id}>
												<TableCell>{quiz.quiz_order}</TableCell>
												<TableCell>
													<Typography variant="body2" sx={{ fontWeight: 500 }}>
														{quiz.question}
													</Typography>
													<Box
														sx={{
															mt: 1,
															display: "grid",
															gridTemplateColumns: "1fr 1fr",
															gap: 1,
														}}>
														<Typography
															variant="caption"
															color="text.secondary">
															A: {quiz.option_a}
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary">
															B: {quiz.option_b}
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary">
															C: {quiz.option_c}
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary">
															D: {quiz.option_d}
														</Typography>
													</Box>
												</TableCell>
												<TableCell>
													<Typography
														variant="body2"
														sx={{
															fontWeight: 600,
															textTransform: "uppercase",
														}}>
														{quiz.correct_answer}
													</Typography>
												</TableCell>
												<TableCell align="right">
													<IconButton
														size="small"
														onClick={() => handleEditQuiz(quiz)}
														sx={{ mr: 1 }}>
														<EditIcon size={16} />
													</IconButton>
													<IconButton
														size="small"
														color="error"
														onClick={() => handleDeleteQuiz(quiz.id)}>
														<TrashIcon size={16} />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						) : (
							<Box sx={{ textAlign: "center", py: 4 }}>
								<Typography color="text.secondary" sx={{ mb: 2 }}>
									No quizzes found for this content
								</Typography>
								<Button
									variant="outlined"
									startIcon={<PlusIcon size={18} />}
									onClick={handleAddQuiz}>
									Add Your First Quiz
								</Button>
							</Box>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};

export default GameContentQuizzes;
